import { FC, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Stack } from '@mui/material';
import { QueryKey } from '@/lib/query-client';
import { getMcpToolsByServerId } from '@/lib/services/mcp.service';
import AccordionButton from '@/components/accordion-button';
import Text from '@/components/text';
import Loader from '@/components/loader';
import useQueryHelper from '@/hooks/use-query-helper';
import SelectMulti from '@/components/form/select-multi';
import TextInput from '@/components/form/text-input';
import { UiOption } from '@/lib/helpers';
import InlineToast from '@/components/toasts/inline';
import { getMcpToolActionOptions } from '@/lib/models/mcp-tool';
import { McpToolModel } from '@/lib/models/mcp-tool/mcp-tool.model';
import ClassificationActionChip from '@/components/chip/classification-action-chip';

interface Props {
  id: string;
  defaultToolId?: string;
}

const DEFAULT_ACTION_OPTIONS = getMcpToolActionOptions();

export const Tools: FC<Props> = ({ id, defaultToolId }) => {
  const toolQuery = useQuery([QueryKey.McpToolList, id], async () => getMcpToolsByServerId(id));
  const { showLoader } = useQueryHelper(toolQuery);
  const { data: tools = [] as McpToolModel[] } = toolQuery;
  const [search, setSearch] = useState('');
  const [actionOptions, setActionOptions] = useState<UiOption[]>(DEFAULT_ACTION_OPTIONS);
  const hasTools = !!tools.length;

  const filteredTools = tools
    .filter(({ name, description, classification: { action } }) => {
      if (
        search &&
        !name.toLowerCase().includes(search.toLowerCase()) &&
        !description.toLowerCase().includes(search.toLowerCase())
      ) {
        return false;
      }
      if (!actionOptions.some(({ value }) => value === action.toString())) {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const emptyMessage = hasTools
    ? 'No tools match your search criteria'
    : 'No tools available for this MCP Server';

  const hasFilteredTools = !!filteredTools.length;

  useEffect(() => {
    if (!defaultToolId || !tools.length) {
      return;
    }

    const defaultTool = tools.find(({ id: toolId }) => toolId === defaultToolId);
    if (!defaultTool) {
      return;
    }

    setSearch(defaultTool.name);
  }, [defaultToolId, tools]);

  if (showLoader) {
    return <Loader />;
  }

  return (
    <Stack gap={1} mt={2}>
      <Stack width="100%" gap={1} flexWrap="wrap">
        <TextInput
          placeholder="Search Tools"
          name="keyword"
          value={search}
          onChange={setSearch}
          size="small"
          clearable
          startIcon="search"
          disabled={!hasTools}
        />
        <SelectMulti
          name="blockActions"
          options={DEFAULT_ACTION_OPTIONS}
          value={actionOptions.map(({ value }) => value)}
          onChange={(options) =>
            setActionOptions(options.map(({ value }) => ({ label: value, value }) as UiOption))
          }
          size="small"
          label="Actions"
          disabled={!hasTools}
        />
      </Stack>
      <Stack gap={1}>
        {!hasFilteredTools && <InlineToast message={emptyMessage} level="info" />}
        {hasFilteredTools &&
          filteredTools.map(({ id, name, description, classification: { action } }) => {
            const isDefaultTool = id === defaultToolId;

            const TitleEl = (
              <Stack direction="row" width="100%">
                <Text>{name}</Text>
                <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
                  <ClassificationActionChip value={action} size="small" />
                </Stack>
              </Stack>
            );

            return (
              <AccordionButton title={TitleEl} key={id} defaultOpen={isDefaultTool}>
                {description}
              </AccordionButton>
            );
          })}
      </Stack>
    </Stack>
  );
};

export default Tools;
